import { twJoin } from 'tailwind-merge';
import { pathMatchIgnoreLocale } from '@/ui/utils/localization-helpers/localization';
import { pathsHideBanner } from '@/constants/paths';
import {
  PieceFragment,
  RichTextFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import {
  ButtonColorFamily,
  getButtonColorFamily,
} from '@/ui/components/shared/Button/button-utils';
import { BANNERS_COOKIE_NAME } from '@/utils/constants';
import { getCookieValue } from '@/utils/cookie-helper';
import { slugify } from '@/utils/url-helper';
import { BannerTile } from './types/banner-tile';

const BANNERS_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

interface StoredBanner {
  id: string;
  isDismissed: boolean;
}

/**
 * Banner dismissals live in the `nu_banners` cookie as a JSON array of { id, isDismissed } — the
 * same shape the previous localStorage entry used, stored raw (not URL-encoded) like the app's
 * other JSON cookies. A cookie (not localStorage) so the server can read it during SSR and render
 * the banner already-dismissed, avoiding the flash of it appearing and then animating away after
 * hydration. Each campaign is tracked independently.
 */
export function getBannersFromRawCookie(rawCookieValue?: string): StoredBanner[] {
  if (!rawCookieValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawCookieValue);
    return Array.isArray(parsed)
      ? parsed.filter(
        (banner): banner is StoredBanner =>
          typeof banner?.id === 'string' && typeof banner?.isDismissed === 'boolean',
      )
      : [];
  } catch {
    return [];
  }
}

/** True when the given campaign is present in the cookie and marked dismissed. */
export function isBannerDismissed(rawCookieValue: string | undefined, bannerId: string): boolean {
  return getBannersFromRawCookie(rawCookieValue).some(
    (banner) => banner.id === bannerId && banner.isDismissed,
  );
}

/**
 * Marks a banner campaign as dismissed client-side by upserting { id, isDismissed: true } into the
 * `nu_banners` cookie. Dismissing one campaign never touches another. Client-only.
 */
export function setBannerAsDismissed(bannerId: string): void {
  const others = getBannersFromRawCookie(getCookieValue(BANNERS_COOKIE_NAME)).filter(
    (banner) => banner.id !== bannerId,
  );
  const next: StoredBanner[] = [...others, { id: bannerId, isDismissed: true }];
  const value = JSON.stringify(next);

  document.cookie = `${BANNERS_COOKIE_NAME}=${value}; path=/; max-age=${BANNERS_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

export function shouldHideBannerOnPage(currentPath: string) {
  return pathsHideBanner.some((path) => pathMatchIgnoreLocale(currentPath, path));
}

interface OfferEndedColors {
  backgroundColor: string;
  color: string;
  borderColor: string;
}

// The OFFER ENDED pill is a muted version of the CTA button, keyed to the button's color family.
const OFFER_ENDED_BY_FAMILY: Record<ButtonColorFamily, OfferEndedColors> = {
  yellow: { backgroundColor: '#745500', color: '#FECB3F', borderColor: '#745500' },
  white: { backgroundColor: '#F3F3F3', color: '#6E6E6E', borderColor: '#949494' },
  black: { backgroundColor: '#333333', color: '#B2B2B2', borderColor: '#4D4D4D' },
};

/**
 * OFFER ENDED colors derived from the CTA button's own `variant` (falls back to the yellow family
 * when the CTA has no variant), so the expired pill visually matches the campaign's button.
 */
export function getOfferEndedColors(cta?: PieceFragment): OfferEndedColors {
  const variant = extractComponentFromChunk(cta?.components, 'variant', 'SelectionContent')
    ?.options?.[0]?.value;

  return OFFER_ENDED_BY_FAMILY[getButtonColorFamily(variant)];
}

interface BannerLayoutParams {
  isLeftAligned: boolean;
  hasImage: boolean;
  showCountdown: boolean;
  hasCta: boolean;
  isOfferEnded: boolean;
}

/**
 * Resolves the banner's arrangement from the alignment + what content is present. Kept out of the
 * component so the branching doesn't add to its cognitive complexity.
 *
 * - Center (or any layout with an image): image + content stacked/centered; countdown and CTA below.
 * - Left with no image splits horizontally — text on the left and, on the right, the countdown (or,
 *   when there's no countdown, the button). With a countdown present the button drops below the text.
 *   `row`/`content` are the Tailwind classes; `countdownRight`/`ctaRight` say which slot to render in.
 */
export function getBannerLayout({
  isLeftAligned,
  hasImage,
  showCountdown,
  hasCta,
  isOfferEnded,
}: BannerLayoutParams) {
  const canSplit = isLeftAligned && !hasImage;
  const countdownRight = canSplit && showCountdown;
  const ctaRight = canSplit && !showCountdown && hasCta;
  const offerEndedRight = canSplit && isOfferEnded;
  const isSplit = countdownRight || ctaRight || offerEndedRight;
  const isTextCentered = !isLeftAligned && !hasImage;

  const stackedJustify = isLeftAligned ? 'sm:justify-start' : 'justify-center';
  const row = isSplit
    ? 'flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between'
    : twJoin('flex flex-col gap-10 sm:flex-row', stackedJustify);

  const content = twJoin(
    'flex flex-col',
    isTextCentered ? 'items-center text-center' : 'items-start text-left',
    canSplit && !isSplit && 'w-full',
  );

  return {
    row, content, countdownRight, ctaRight, offerEndedRight, isSplit,
  };
}

/**
 * Resolves the banner's text alignment. An explicit CMS `text-alignment` option wins; otherwise it
 * falls back to the historic image-driven default (image → left, no image → centered).
 */
export function getBannerAlignment(tile: BannerTile, hasImage: boolean): 'left' | 'center' {
  const value = tile['text-alignment']?.options?.[0]?.value;

  if (value === 'left' || value === 'center') {
    return value;
  }

  return hasImage ? 'left' : 'center';
}

/** True when a rich-text field has any non-whitespace content to render. */
export function hasRichTextContent(richText?: RichTextFragment): boolean {
  return Boolean(richText?.plainText?.join('').trim());
}

/**
 * Builds a stable identifier for a banner campaign from its content — the slugified title (using
 * `_` as the separator) plus the start/end dates as epoch timestamps. Used to key the persisted
 * dismissal so a NEW campaign reappears even if a previous one was closed. Returns undefined when
 * there is no banner content to identify.
 */
export function getBannerId(tile?: BannerTile): string | undefined {
  if (!tile || Object.keys(tile).length === 0) {
    return undefined;
  }

  const title = tile.title?.plainText?.join(' ');
  const startMs = Date.parse(tile['start-date']?.datetime ?? '');
  const endMs = Date.parse(tile['end-date']?.datetime ?? '');

  const parts = [
    title ? slugify(title).replaceAll('-', '_') : undefined,
    Number.isNaN(startMs) ? undefined : startMs,
    Number.isNaN(endMs) ? undefined : endMs,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join('_') : undefined;
}
